/**
 * WebSocket reconnect utilities with exponential backoff and jitter.
 */

const NON_RECONNECTABLE_CODES = new Set([
  1000, // Normal closure (intentional disconnect)
  4001, // Token expired (server auth failure)
]);

export const RECONNECT_CONFIG = {
  initialDelay: 1000, // 1s
  maxDelay: 30_000, // 30s
  multiplier: 2,
  jitterFactor: 0.2, // +/- 20%
  maxAttempts: 10, // Stop after 10 attempts (~5 min total)
} as const;

/**
 * Calculate reconnect delay with exponential backoff and jitter.
 * Returns: min(initialDelay * 2^attempt, maxDelay) * random(0.8, 1.2)
 */
export function calculateBackoff(attempt: number): number {
  const { initialDelay, maxDelay, multiplier, jitterFactor } = RECONNECT_CONFIG;
  const baseDelay = Math.min(
    initialDelay * Math.pow(multiplier, attempt),
    maxDelay,
  );
  const jitter = 1 + (Math.random() * 2 - 1) * jitterFactor;
  return Math.round(baseDelay * jitter);
}

/**
 * Determine whether we should attempt to reconnect based on close code and intent.
 */
export function shouldReconnect(
  closeCode: number,
  wasIntentional: boolean,
): boolean {
  if (wasIntentional) return false;
  if (NON_RECONNECTABLE_CODES.has(closeCode)) return false;
  return true;
}
